import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, TextField, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment, MenuItem, Chip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import axios from 'axios';

const API_URL = '/api/choferes';
const emptyForm = { nombres: '', apellidos: '', tipo_documento: 'DNI', numero_documento: '', licencia: '', estado: 1 };

const Choferes = () => {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);

  const fetch = async () => {
    const res = await axios.get(API_URL);
    setRows(res.data);
  };
  useEffect(() => { fetch(); }, []);

  const handleOpen = (row = null) => {
    if (row) { setEditId(row.id); setForm({ nombres: row.nombres, apellidos: row.apellidos, tipo_documento: row.tipo_documento, numero_documento: row.numero_documento, licencia: row.licencia || '', estado: row.estado }); }
    else { setEditId(null); setForm(emptyForm); }
    setOpen(true);
  };
  const handleClose = () => { setOpen(false); setEditId(null); setForm(emptyForm); };

  const handleSave = async () => {
    if (!form.nombres.trim() || !form.apellidos.trim()) { alert('Nombres y apellidos son obligatorios'); return; }
    if (!form.numero_documento.trim()) { alert('Número de documento es obligatorio'); return; }
    try {
      if (editId) await axios.put(`${API_URL}/${editId}`, form);
      else await axios.post(API_URL, form);
      fetch();
      handleClose();
    } catch (err) { alert(err.response?.data?.error || 'Error al guardar'); }
  };

  const toggleEstado = async (row) => {
    try {
      await axios.put(`${API_URL}/${row.id}`, { ...row, licencia: row.licencia || '', estado: row.estado ? 0 : 1 });
      fetch();
    } catch { alert('Error al cambiar estado'); }
  };

  const filtered = rows.filter(r =>
    `${r.nombres} ${r.apellidos} ${r.numero_documento} ${r.licencia || ''}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 1, mb: 4, width: '100%' }}>
      <Box sx={{ width: { xs: '100%', sm: '95%', md: '90%' }, maxWidth: 1400 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 'bold', color: '#222' }}>Choferes</Typography>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField placeholder="Buscar..." value={search} onChange={e => setSearch(e.target.value)} size="small"
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} sx={{ flex: 1 }} />
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>Nuevo Chofer</Button>
        </Box>
        <TableContainer component={Paper} sx={{ borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#1976d2' }}>
                {['ID', 'Nombres', 'Apellidos', 'Tipo Doc.', 'Nro. Documento', 'Licencia', 'Estado', 'Acciones'].map(h => (
                  <TableCell key={h} sx={{ color: '#fff', fontWeight: 'bold' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map(r => (
                <TableRow key={r.id} hover>
                  <TableCell>{r.id}</TableCell>
                  <TableCell>{r.nombres}</TableCell>
                  <TableCell>{r.apellidos}</TableCell>
                  <TableCell>{r.tipo_documento}</TableCell>
                  <TableCell>{r.numero_documento}</TableCell>
                  <TableCell>{r.licencia || '-'}</TableCell>
                  <TableCell>
                    <Chip label={r.estado ? 'Activo' : 'Inactivo'} color={r.estado ? 'success' : 'default'} size="small"
                      onClick={() => toggleEstado(r)} sx={{ cursor: 'pointer' }} />
                  </TableCell>
                  <TableCell>
                    <IconButton color="primary" size="small" onClick={() => handleOpen(r)}><EditIcon /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && <TableRow><TableCell colSpan={8} align="center">Sin registros</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
          <DialogTitle><Typography variant="h6" fontWeight="bold">{editId ? 'Editar Chofer' : 'Nuevo Chofer'}</Typography></DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField label="Nombres" value={form.nombres} onChange={e => setForm(f => ({ ...f, nombres: e.target.value.toUpperCase() }))} fullWidth required />
                <TextField label="Apellidos" value={form.apellidos} onChange={e => setForm(f => ({ ...f, apellidos: e.target.value.toUpperCase() }))} fullWidth required />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField select label="Tipo Documento" value={form.tipo_documento} onChange={e => setForm(f => ({ ...f, tipo_documento: e.target.value }))} sx={{ minWidth: '10em' }}>
                  <MenuItem value="DNI">DNI</MenuItem>
                  <MenuItem value="RUC">RUC</MenuItem>
                  <MenuItem value="CE">Carnet Extranjería</MenuItem>
                  <MenuItem value="PAS">Pasaporte</MenuItem>
                </TextField>
                <TextField label="Nro. Documento" value={form.numero_documento} onChange={e => setForm(f => ({ ...f, numero_documento: e.target.value }))} fullWidth required />
              </Box>
              <TextField label="Licencia de conducir" value={form.licencia} onChange={e => setForm(f => ({ ...f, licencia: e.target.value.toUpperCase() }))}
                fullWidth helperText="Sin guiones, ej: Q33257279" />
              {editId && (
                <TextField select label="Estado" value={form.estado} onChange={e => setForm(f => ({ ...f, estado: Number(e.target.value) }))}>
                  <MenuItem value={1}>Activo</MenuItem>
                  <MenuItem value={0}>Inactivo</MenuItem>
                </TextField>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} startIcon={<CloseIcon />} color="error" variant="contained">Cancelar</Button>
            <Button onClick={handleSave} variant="contained" startIcon={<SaveIcon />}>Guardar</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default Choferes;
